import type { PrinterProfile, Warning } from '@tsagko/label-contracts'

/**
 * Single chokepoint for all text flowing into ZPL output (design-spec §8.6).
 *
 * Contract:
 *   - Strips ZPL command characters: `^` and `~` are silently replaced with `_`.
 *     (Silent escape: no per-occurrence warning — too chatty for legitimate
 *     text that happens to contain these characters.)
 *   - Strips control characters `\x00`–`\x1F` except `\t`, `\n`, `\r`. Silent.
 *   - Encoding handling:
 *       UTF8 / UTF8-CJK → pass through (Zebra accepts UTF-8 with ^CI28).
 *       CP850          → pass through, but emit ONE `FONT_SUBSTITUTION`
 *                        warning if any code point > U+00FF is present.
 *   - Never throws on any input.
 *   - Pure: no mutation of input, no I/O, no globals.
 *   - The returned `value` is guaranteed not to contain `^` or `~`.
 */
export function escapeZplText(
  input: string,
  encoding: PrinterProfile['encoding']
): { value: string; warnings: Warning[] } {
  const warnings: Warning[] = []

  // Build the escaped value in a single pass to keep behaviour predictable
  // for very long inputs and to make the invariants easy to audit.
  let value = ''
  let hasCp850OutOfRange = false

  for (let i = 0; i < input.length; i++) {
    const code = input.charCodeAt(i)

    // Strip control chars except tab, LF, CR (silent).
    if (code <= 0x1f && code !== 0x09 && code !== 0x0a && code !== 0x0d) {
      continue
    }

    // Silent escape of ZPL command characters.
    if (code === 0x5e /* ^ */ || code === 0x7e /* ~ */) {
      value += '_'
      continue
    }

    // CP850 covers code points U+0000..U+00FF. Anything above that cannot be
    // represented and will be substituted by the printer (or rendered as a
    // box). We emit one warning regardless of how many out-of-range code
    // points are present, to avoid a chatty warning stream.
    //
    // CONTRACT (#137, decided 2026-07-16): `encoding: 'CP850'` gates ONLY
    // this glyph-coverage warning. It does NOT transcode wire bytes — the
    // pipeline unconditionally emits ^CI28 (UTF-8) and sends UTF-8 bytes
    // (see generate-zpl.ts). A profile whose firmware genuinely cannot do
    // UTF-8 is unsupported until a real such device shows up; no transcoder
    // exists anywhere in this codebase by design.
    if (encoding === 'CP850' && code > 0xff) {
      hasCp850OutOfRange = true
    }

    value += input[i]
  }

  if (hasCp850OutOfRange) {
    warnings.push({
      code: 'FONT_SUBSTITUTION',
      severity: 'warn',
      message:
        'text contains characters outside CP850; printer may substitute or render boxes',
    })
  }

  return { value, warnings }
}
